import type { GetStaticProps, GetStaticPropsContext } from 'next';
import ErrorPage from 'next/error';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-export-i18n';
import { SWRConfig } from 'swr';
import useSWRImmutable from 'swr/immutable';

import ButtonLink from '@/components/buttons/btn-link';
import RelatedCompany from '@/components/molecules/RelatedCompany';
import FilterOverlay from '@/layouts/FilterOverlay';
import Footer from '@/layouts/Footer';
import { Meta } from '@/layouts/Meta';
import { Main } from '@/templates/Main';
import type CompanyProps from '@/types/Company.type';
import { AppConfig } from '@/utils/AppConfig';

type IIndexProps = {
  preview?: boolean | undefined;
  fallback: any;
};

const fetcher = (url: string) => fetch(url).then((res) => res.json());
const FEATURED_COMPANIES = `${process.env.NEXT_PUBLIC_API_URL}/company/featured-companies`;

const FeaturedCompany = () => {
  const { t } = useTranslation();
  const router = useRouter();
  const { data: companies } = useSWRImmutable(FEATURED_COMPANIES, fetcher);

  if (!companies || (!router.isFallback && companies.length < 0)) {
    return <ErrorPage statusCode={404} />;
  }

  return (
    <Main
      screenSize="mx-auto max-w-screen-xl justify-between"
      meta={
        <Meta
          title={`Featured Company | ${AppConfig.site_name}`}
          description={AppConfig.description}
          canonical={`${process.env.NEXT_PUBLIC_WEB_URL}/featured-companies`}
        />
      }
    >
      <div className="p-5">
        <h2 className="mt-6 mb-3 text-2xl font-medium">
          {t('nav.featuredCompanies')}
        </h2>
        <div className="my-7 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {companies.map((company: CompanyProps) => (
            <Link href={`/${company.slug}`} key={company.id}>
              <a href={`/${company.slug}`}>
                <RelatedCompany company={company} />
              </a>
            </Link>
          ))}
        </div>
        <div className="my-10 flex items-center justify-center">
          <ButtonLink
            label="Explore More Companies"
            link="/"
            isOpenNewTab={false}
          />
        </div>
      </div>

      <div className="px-5">
        <hr />
        <Footer />
      </div>
      <FilterOverlay />
    </Main>
  );
};

export const getStaticProps: GetStaticProps = async (
  context: GetStaticPropsContext
) => {
  const isPreview = (await context.preview) || false;
  const homeResp = await fetcher(FEATURED_COMPANIES);

  return {
    props: {
      isPreview,
      fallback: {
        [FEATURED_COMPANIES]: homeResp,
      },
    },
  };
};

const FeaturedCompanyPage = (props: IIndexProps) => {
  return (
    <SWRConfig value={{ fallback: props.fallback }}>
      <FeaturedCompany />
    </SWRConfig>
  );
};

export default FeaturedCompanyPage;
